import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { MyActionsProvider } from "./context/MyActionsContext";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ForcePasswordChange from "./components/ForcePasswordChange";
import HomePage from "./pages/HomePage";
import AssetsPage from "./pages/AssetsPage";
import AssetDetailPage from "./pages/AssetDetailPage";
import AssetTreePage from "./pages/AssetTreePage";
import CheckRunnerPage from "./pages/CheckRunnerPage";
import FaultReportPage from "./pages/FaultReportPage";
import FaultsPage from "./pages/FaultsPage";
import ScanPage from "./pages/ScanPage";
import NfcBulkPage from "./pages/NfcBulkPage";
import TagResolvePage from "./pages/TagResolvePage";
import QuickDataPage from "./pages/QuickDataPage";
import PeoplePage from "./pages/PeoplePage";
import MaintenancePage from "./pages/MaintenancePage";
import MaintenanceJobPage from "./pages/MaintenanceJobPage";
import ServiceHistoryPage from "./pages/ServiceHistoryPage";
import PartsStockPage from "./pages/PartsStockPage";
import ChecksPage from "./pages/ChecksPage";
import OverdueChecksPage from "./pages/OverdueChecksPage";
import DocumentsPage from "./pages/DocumentsPage";
import AuditPage from "./pages/AuditPage";
import AdminPage from "./pages/AdminPage";
import AdminDataManagerPage from "./pages/AdminDataManagerPage";
import MorePage from "./pages/MorePage";
import AssetTransferQueuePage from "./pages/AssetTransferQueuePage";
import PolicyWorkspacePage from "./pages/PolicyWorkspacePage";
import PartOrdersPage from "./pages/PartOrdersPage";
import MyProfilePage from "./pages/MyProfilePage";
import MyEquipmentPage from "./pages/MyEquipmentPage";
import EquipmentAdminPage from "./pages/EquipmentAdminPage";
import GaragePage from "./pages/GaragePage";
import PendingApprovalsPage from "./pages/PendingApprovalsPage";
import RamsPage from "./pages/RamsPage";
import TrainingPage from "./pages/TrainingPage";
import PpePage from "./pages/PpePage";
import CompliancePage from "./pages/CompliancePage";
import DeptCompliancePage from "./pages/DeptCompliancePage";
import DepartmentsPage from "./pages/DepartmentsPage";
import HavsPage from "./pages/HavsPage";
import PolicyLibraryPage from "./pages/PolicyLibraryPage";
import PolicyDetailPage from "./pages/PolicyDetailPage";
import BookReaderPage from "./pages/BookReaderPage";
import PolicyManagerPage from "./pages/PolicyManagerPage";
import PolicyMatrixPage from "./pages/PolicyMatrixPage";
import MyRamsPage from "./pages/MyRamsPage";
import PlatformAdminPage from "./pages/PlatformAdminPage";
import ReportsPage from "./pages/ReportsPage";
import InsurancePage from "./pages/InsurancePage";
import LolerPage from "./pages/LolerPage";

function Protected({ children }) {
  const { session, loading, profile } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  // Force a password change before any app access when an admin issued a temporary password.
  if (profile?.must_change_password) return <ForcePasswordChange />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  const P = (el) => <Protected>{el}</Protected>;
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/" element={P(<HomePage />)} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/scan" element={P(<ScanPage />)} />
      <Route path="/nfc/programme" element={P(<NfcBulkPage />)} />
      <Route path="/t/:token" element={P(<TagResolvePage />)} />
      <Route path="/assets" element={P(<AssetsPage />)} />
      <Route path="/assets/tree" element={P(<AssetTreePage />)} />
      <Route path="/assets/:id" element={P(<AssetDetailPage />)} />
      <Route path="/assets/:id/check" element={P(<CheckRunnerPage />)} />
      <Route path="/faults" element={P(<FaultsPage />)} />
      <Route path="/faults/report" element={P(<FaultReportPage />)} />
      <Route path="/maintenance" element={P(<MaintenancePage />)} />
      <Route path="/maintenance/history" element={P(<ServiceHistoryPage />)} />
      <Route path="/maintenance/parts" element={P(<PartsStockPage />)} />
      <Route path="/maintenance/part-orders" element={P(<PartOrdersPage />)} />
      <Route path="/maintenance/jobs/:id" element={P(<MaintenanceJobPage />)} />
      <Route path="/quick-data" element={P(<QuickDataPage />)} />
      <Route path="/havs" element={P(<HavsPage />)} />
      <Route path="/checks" element={P(<ChecksPage />)} />
      <Route path="/checks/overdue" element={P(<OverdueChecksPage />)} />
      <Route path="/documents" element={P(<DocumentsPage />)} />
      <Route path="/rams" element={<Navigate to="/policies?class=rams" replace />} />
      <Route path="/policies" element={P(<PolicyLibraryPage />)} />
      <Route path="/my-rams" element={P(<MyRamsPage />)} />
      <Route path="/policies/book/:id" element={P(<BookReaderPage />)} />
      <Route path="/policies/:id" element={P(<PolicyDetailPage />)} />
      <Route path="/policy-manager" element={P(<PolicyManagerPage />)} />
      <Route path="/policy-manager/doc/:id" element={P(<PolicyWorkspacePage />)} />
      <Route path="/policy-matrix" element={P(<PolicyMatrixPage />)} />
      <Route path="/training" element={P(<TrainingPage />)} />
      <Route path="/ppe" element={P(<PpePage />)} />
      <Route path="/compliance" element={P(<DeptCompliancePage />)} />
      <Route path="/compliance/roles" element={P(<CompliancePage />)} />
      <Route path="/reports" element={P(<ReportsPage />)} />
      <Route path="/insurance" element={P(<InsurancePage />)} />
      <Route path="/loler" element={P(<LolerPage />)} />
      <Route path="/departments" element={P(<DepartmentsPage />)} />
      <Route path="/people" element={P(<PeoplePage />)} />
      <Route path="/audit" element={P(<AuditPage />)} />
      <Route path="/admin" element={P(<AdminPage />)} />
      <Route path="/admin/data" element={P(<AdminDataManagerPage />)} />
      <Route path="/platform" element={P(<PlatformAdminPage />)} />
      <Route path="/more" element={P(<MorePage />)} />
      <Route path="/asset-transfers" element={P(<AssetTransferQueuePage />)} />
      <Route path="/my-profile" element={P(<MyProfilePage />)} />
      <Route path="/my-equipment" element={P(<MyEquipmentPage />)} />
      <Route path="/equipment-admin" element={P(<EquipmentAdminPage />)} />
      <Route path="/garage" element={P(<GaragePage />)} />
      <Route path="/training/pending" element={P(<PendingApprovalsPage />)} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MyActionsProvider>
          <AppRoutes />
        </MyActionsProvider>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
